import type { Building } from "building-types/building/buildingTypes";
import type { LevelExtrusion, LevelPerimeters } from "building-types/levels/levelPerimeters";
import { levelPerimetersNew } from "building-types/levels/levelPerimeters";
import type { RWTable, Table } from "common/data/table";
import { planarGraphVertexStripToPolyLine2 } from "geom/planarGraph";
import { polyLine2MergeCoincidentVertices, polyLine2MergeCollinearEdges, polyLine2Simplify } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { sortedLevels } from "../elements/level/levelTools";
import memo from "../utils/memo";

function select({ levels, planarGraphs, planarGraphPerimeters }: Building) {
  return {
    levels,
    planarGraphs,
    planarGraphPerimeters,
  };
}

function generate({ levels, planarGraphs, planarGraphPerimeters }: ReturnType<typeof select>): {
  floorPlates: Table<LevelPerimeters>;
  levelExtrusions: Table<LevelExtrusion>;
} {
  const levelExtrusions: RWTable<LevelExtrusion> = {};
  const floorPlates: RWTable<LevelPerimeters> = {};

  const orderedLevels = sortedLevels(levels);
  for (let i = 0; i < orderedLevels.length; i++) {
    const level = orderedLevels[i];
    const levelId = level.id;
    const graph = planarGraphs[levelId];
    const { allPerimeters, uncoveredPerimeters, coveredPerimeters } = planarGraphPerimeters[levelId];

    const allPerimeterPolyLine2s: PolyLine2[] = [];

    for (const faceVertexList of allPerimeters.faceVertices) {
      const perimeter = planarGraphVertexStripToPolyLine2(graph.planarGraph, faceVertexList, true);
      allPerimeterPolyLine2s.push(perimeter);
    }

    const perimeterPolyLine2s: PolyLine2[] = [];
    const perimeterPolyLine2sWithoutExteriorFaces: PolyLine2[] = [];

    for (let j = 0, jl = uncoveredPerimeters.faceVertices.length; j < jl; j++) {
      let perimeter = planarGraphVertexStripToPolyLine2(graph.planarGraph, uncoveredPerimeters.faceVertices[j]);
      perimeter = polyLine2Simplify(perimeter);
      perimeterPolyLine2s.push(perimeter);

      if (coveredPerimeters.faceVertices[j] !== undefined) {
        let epl = planarGraphVertexStripToPolyLine2(graph.planarGraph, coveredPerimeters.faceVertices[j]);
        epl = polyLine2MergeCollinearEdges(epl);
        epl = polyLine2MergeCoincidentVertices(epl);
        perimeterPolyLine2sWithoutExteriorFaces.push(epl);
      } else {
        perimeterPolyLine2sWithoutExteriorFaces.push(perimeter);
      }
    }

    floorPlates[levelId] = levelPerimetersNew(
      uncoveredPerimeters,
      perimeterPolyLine2s,
      perimeterPolyLine2sWithoutExteriorFaces,
      allPerimeterPolyLine2s,
    );

    // Use level.levelAbove instead of orderedLevels[i + 1] to ensure compatibility with multiple reporting units.
    // In that case, the next highest floor plate could be in a different reporting unit.
    const levelAbove = level.levelAbove ? levels[level.levelAbove] : undefined;
    levelExtrusions[levelId] = {
      perimeters: perimeterPolyLine2sWithoutExteriorFaces,
      // the addition of the garage datum handles the lower elevation of the garage floor on such levels
      minZ: level.floorElevation + level.levelDatumsGenerated.garage || 0,
      maxZ: levelAbove ? levelAbove.floorElevation : level.floorElevation + level.topOfWall,
    };
  }

  return {
    floorPlates,
    levelExtrusions,
  };
}

export default memo(select, generate);
